import { useState } from 'react';
import { creatorbase } from '@round/api';
import { isReceivedCampaign } from 'Modules/Plans/Campaigns/helpers';
import { buildInstagramUserUrl, showNotification } from 'helpers';
import useRefreshProjectCampaignStats from '../../../hooks/useRefreshProjectCampaignStats';
import { backupPostsStatuses } from 'Modules/Plans/Posts/helpers';
import { CampaignTableRow } from '../../ProjectCampaignsTable/ProjectCampaignsTable';
import BackupCreatorsExpandableBar from '../../BackupCreatorsExpandableBar/BackupCreatorsExpandableBar';
import InstagramPostsTable from './InstagramPostsTable';
import useProjectDetailsInstagramPosts, {
    InstagramPostsTableRow,
} from '../../../hooks/posts/useProjectDetailsInstagramPosts';
import useUpdateProjectDetailsPost from '../../../hooks/posts/useUpdateProjectDetailsPost';
import DeletePostModal from '../../../Posts/DeletePostModal/DeletePostModal';
import PostStatsModal, { PostStatsModalAccount } from 'Modules/Plans/Posts/components/PostStatsModal/PostStatsModal';
import { useInvalidatePosts } from '@round/creatorbase-helpers/api-hooks';
import { getProjectDetailsPostsParams } from '../../../hooks/posts/getProjectDetailsPostsParams';

type Props = {
    campaign: CampaignTableRow;
    isBackupExpanded: boolean;
    onBackupExpandToggle: () => void;
    isPaymentEnabled?: boolean;
    // Adding creators belongs to the campaign, not the posts read, so the
    // handler is owned by the parent and passed through to the table's empty state.
    onAddCreatorsToCampaign?: (campaign: CampaignTableRow) => void;
    onCreatePost: () => void;
};

const isBackupPost = (post: InstagramPostsTableRow) =>
    backupPostsStatuses.includes(post.status as creatorbase.BackupPostStatus);

export default function InstagramPosts({
    campaign,
    isBackupExpanded,
    onBackupExpandToggle,
    isPaymentEnabled,
    onAddCreatorsToCampaign,
    onCreatePost,
}: Props) {
    const { data, isLoading, hasError, isAccountDataLoading } = useProjectDetailsInstagramPosts(campaign.id);

    const refreshAllStats = useRefreshProjectCampaignStats();
    const { updatePost } = useUpdateProjectDetailsPost(campaign.id, 'instagram');
    const isReceived = isReceivedCampaign(campaign);
    const invalidatePosts = useInvalidatePosts();
    // A status change moves a post between the active and backup tables, so
    // refresh the campaign's posts when the status cell reports an update. It
    // also shifts the post in/out of the active set the headline and
    // campaign-row stats are aggregated from, so refresh those too.
    const handlePostStatusUpdated = () => {
        invalidatePosts(getProjectDetailsPostsParams(campaign.id));
        refreshAllStats(campaign.id, 'instagram').catch(() => {
            showNotification('Could not refresh data', 'error');
        });
    };

    // A single fetch returns every post for the campaign; the active table shows
    // everything that isn't a backup status, the backup bar shows the rest. The
    // status lists are exhaustive over manual statuses, so no post is dropped.
    const activePosts = data.filter((post) => !isBackupPost(post));
    const backupPosts = data.filter(isBackupPost);

    const [postToDelete, setPostToDelete] = useState<InstagramPostsTableRow | null>(null);
    // Kept as an id so the open modal follows the refetched post and its account as they load
    const [statsPostId, setStatsPostId] = useState<InstagramPostsTableRow['id'] | null>(null);
    const statsPost = data.find((post) => post.id === statsPostId);

    return (
        <>
            <InstagramPostsTable
                isPaymentEnabled={isPaymentEnabled}
                data={activePosts}
                campaign={campaign}
                isLoading={isLoading}
                isAccountDataLoading={isAccountDataLoading}
                hasError={hasError}
                onCreatePost={isReceived ? undefined : onCreatePost}
                onAddCreatorsToCampaign={isReceived ? undefined : onAddCreatorsToCampaign}
                onDeletePost={(post) => setPostToDelete(post)}
                onOpenPostStatsModal={(post) => setStatsPostId(post.id)}
                updatePost={updatePost}
                onPostStatusUpdated={handlePostStatusUpdated}
            />

            {campaign.managed_by === 'creatorbase' && !isReceived && (
                <BackupCreatorsExpandableBar
                    isExpanded={isBackupExpanded}
                    onExpandToggle={onBackupExpandToggle}
                    backupCreatorsCount={backupPosts.length}
                >
                    <InstagramPostsTable
                        isPaymentEnabled={isPaymentEnabled}
                        data={backupPosts}
                        campaign={campaign}
                        isLoading={isLoading}
                        isAccountDataLoading={isAccountDataLoading}
                        hasError={hasError}
                        onDeletePost={(post) => setPostToDelete(post)}
                        onOpenPostStatsModal={(post) => setStatsPostId(post.id)}
                        updatePost={updatePost}
                        onPostStatusUpdated={handlePostStatusUpdated}
                    />
                </BackupCreatorsExpandableBar>
            )}

            <DeletePostModal isOpen={!!postToDelete} post={postToDelete} onClose={() => setPostToDelete(null)} />

            {statsPost && (
                <PostStatsModal
                    isOpen
                    post={statsPost}
                    account={getPostStatsModalAccount(statsPost, isAccountDataLoading)}
                    onClose={() => setStatsPostId(null)}
                />
            )}
        </>
    );
}

const getPostStatsModalAccount = (
    post: InstagramPostsTableRow,
    isAccountDataLoading: boolean
): PostStatsModalAccount | null => {
    if (!post.instagram_details?.account_id) {
        return null;
    }

    if (isAccountDataLoading) {
        return { isLoading: true };
    }

    if (!post.user) {
        return null;
    }

    return {
        name: post.user.name,
        handle: post.user.username,
        image: post.image?.avatar_thumb?.cached_url || post.image?.avatar_thumb?.original_url || null,
        profileUrl: buildInstagramUserUrl(post.user.username),
    };
};
